import type { ReactNode } from "react";

import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

import "./ui.css";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: "left" | "right" | "center";
  width?: string;
}

interface TableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  rowClassName?: string;
  /** Enable per-row checkbox selection. */
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /** When set, only rows where this returns true are selectable. */
  isRowSelectable?: (row: T) => boolean;
}

export function Table<T>({
  rows,
  columns,
  rowKey,
  empty,
  onRowClick,
  rowClassName,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isRowSelectable,
}: TableProps<T>) {
  useLocale();

  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  const selectableRows = isRowSelectable
    ? rows.filter((row) => isRowSelectable(row))
    : rows;
  const allSelectableKeys = selectableRows.map((row) => rowKey(row));
  const selectedCount = selectedKeys
    ? allSelectableKeys.filter((k) => selectedKeys.has(k)).length
    : 0;
  const allSelected =
    allSelectableKeys.length > 0 && selectedCount === allSelectableKeys.length;
  const someSelected = selectedCount > 0 && !allSelected;

  function toggleAll() {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(selectedKeys ?? []);
      for (const key of allSelectableKeys) next.delete(key);
      onSelectionChange(next);
    } else {
      const next = new Set(selectedKeys ?? []);
      for (const key of allSelectableKeys) next.add(key);
      onSelectionChange(next);
    }
  }

  function toggleRow(key: string) {
    if (!onSelectionChange) return;
    const next = new Set(selectedKeys ?? []);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  }

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  aria-label={t("table.selectAll")}
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                />
              </th>
            )}
            {columns.map((col) => (
              <th
                key={col.key}
                style={{ textAlign: col.align ?? "left", width: col.width }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            const rowSelectable = !isRowSelectable || isRowSelectable(row);
            return (
              <tr
                key={key}
                className={rowClassName}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
              >
                {selectable && (
                  <td onClick={(e) => e.stopPropagation()}>
                    {rowSelectable ? (
                      <input
                        type="checkbox"
                        aria-label={formatMessage("table.selectRow", { id: key })}
                        checked={selectedKeys?.has(key) ?? false}
                        onChange={() => toggleRow(key)}
                      />
                    ) : null}
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} style={{ textAlign: col.align ?? "left" }}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
